const AUDIO_WAVE_BASE_WIDTH = 1180;
const AUDIO_WAVE_BASE_HEIGHT = 340;
const AUDIO_WAVE_COLUMN_COUNT = 94;
const AUDIO_WAVE_MAX_DOTS_PER_SIDE = 20;
const AUDIO_WAVE_DOT_GAP = 7.4;
const AUDIO_WAVE_ENVELOPE_POWER = 0.62;
const AUDIO_WAVE_ACTIVE_FLOOR = 0.18;
const AUDIO_WAVE_SMOOTHING_FACTOR = 0.18;
const TAU = Math.PI * 2;
const audioWaveCanvasState = new WeakMap<
	HTMLCanvasElement,
	{
		context: CanvasRenderingContext2D;
		gradient: CanvasGradient;
		height: number;
		width: number;
	}
>();

export const clampAudioWaveValue = (value: number, min = 0, max = 1) => {
	return Math.min(max, Math.max(min, value));
};

export const getNextAudioWaveLevel = (current: number, target: number) => {
	const clampedTarget = clampAudioWaveValue(target);
	const next =
		current + (clampedTarget - current) * AUDIO_WAVE_SMOOTHING_FACTOR;

	return Math.abs(next - current) < 0.001 ? clampedTarget : next;
};

export const drawAudioWaveFrame = ({
	canvas,
	cssHeight,
	cssWidth,
	frameTime,
	smoothedLevel
}: {
	canvas: HTMLCanvasElement;
	cssHeight: number;
	cssWidth: number;
	frameTime: number;
	smoothedLevel: number;
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Todo refactor
}) => {
	if (cssWidth <= 0 || cssHeight <= 0) {
		return;
	}

	const context =
		audioWaveCanvasState.get(canvas)?.context ?? canvas.getContext('2d');
	if (!context) {
		return;
	}

	const dpr = window.devicePixelRatio || 1;
	const nextCanvasWidth = Math.max(1, Math.round(cssWidth * dpr));
	const nextCanvasHeight = Math.max(1, Math.round(cssHeight * dpr));

	if (canvas.width !== nextCanvasWidth || canvas.height !== nextCanvasHeight) {
		canvas.width = nextCanvasWidth;
		canvas.height = nextCanvasHeight;
		context.setTransform(dpr, 0, 0, dpr, 0, 0);
	}

	context.clearRect(0, 0, cssWidth, cssHeight);

	const centerY = cssHeight / 2;
	const dotGap = (cssHeight / AUDIO_WAVE_BASE_HEIGHT) * AUDIO_WAVE_DOT_GAP;
	const dotScale = Math.min(
		cssWidth / AUDIO_WAVE_BASE_WIDTH,
		cssHeight / AUDIO_WAVE_BASE_HEIGHT
	);
	const effectiveLevel = clampAudioWaveValue(smoothedLevel);
	const isSilent = effectiveLevel <= 0;
	const animatedLevel = isSilent
		? 0
		: clampAudioWaveValue(
				AUDIO_WAVE_ACTIVE_FLOOR + effectiveLevel * (1 - AUDIO_WAVE_ACTIVE_FLOOR)
			);
	const cachedState = audioWaveCanvasState.get(canvas);
	const gradient =
		cachedState &&
		cachedState.width === cssWidth &&
		cachedState.height === cssHeight
			? cachedState.gradient
			: context.createLinearGradient(0, centerY, cssWidth, centerY);

	if (
		!cachedState ||
		cachedState.width !== cssWidth ||
		cachedState.height !== cssHeight
	) {
		gradient.addColorStop(0, '#F02BBD');
		gradient.addColorStop(0.33, '#8F47FF');
		gradient.addColorStop(0.67, '#00E5FF');
		gradient.addColorStop(1, '#28A8FF');
		audioWaveCanvasState.set(canvas, {
			context,
			gradient,
			height: cssHeight,
			width: cssWidth
		});
	}

	context.fillStyle = gradient;

	for (let index = 0; index < AUDIO_WAVE_COLUMN_COUNT; index += 1) {
		const cx = (cssWidth / (AUDIO_WAVE_COLUMN_COUNT - 1)) * index;
		const normalizedX = index / (AUDIO_WAVE_COLUMN_COUNT - 1);
		const centerDistance = Math.abs(normalizedX - 0.5) / 0.5;
		const envelope = (1 - centerDistance) ** AUDIO_WAVE_ENVELOPE_POWER;
		const pulseA = isSilent
			? 0
			: Math.sin(frameTime * 0.0048 + normalizedX * 24) * 0.5 + 0.5;
		const pulseB = isSilent
			? 0
			: Math.sin(frameTime * 0.0071 - normalizedX * 37) * 0.5 + 0.5;
		const pulseC = isSilent
			? 0
			: Math.sin(frameTime * 0.0115 + normalizedX * 58) * 0.5 + 0.5;
		const energy = isSilent
			? 0.05 + envelope * 0.12
			: clampAudioWaveValue(
					animatedLevel *
						(0.12 +
							envelope *
								(0.22 + animatedLevel * 0.82) *
								(pulseA * 0.5 + pulseB * 0.3 + pulseC * 0.2))
				);
		const visibleDots = Math.max(
			1,
			Math.round(AUDIO_WAVE_MAX_DOTS_PER_SIDE * energy)
		);

		for (let offset = -visibleDots; offset <= visibleDots; offset += 1) {
			const distanceFromCenter = Math.abs(offset) / (visibleDots + 0.5);
			const opacity = clampAudioWaveValue(
				0.18 + (1 - distanceFromCenter) * 0.72 + envelope * 0.08
			);
			const radius =
				(1.05 +
					(1 - distanceFromCenter) * 1.7 +
					envelope * 0.6 +
					animatedLevel * 0.28) *
				dotScale;

			context.globalAlpha = opacity;
			context.beginPath();
			context.arc(cx, centerY + offset * dotGap, radius, 0, TAU);
			context.fill();
		}
	}

	context.globalAlpha = 1;
};
